import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { CartItem } from './cart-container';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import type { ProductType } from '@/types/cart.types';

interface CartProductListProps {
    items: CartItem[];
    onUpdateQuantity: (id: ProductType, delta: number) => void;
    onSeeFeatures?: (id: string) => void;
}

// Product thumbnails keyed by ProductType
const ProductThumbnail = ({ id }: { id: ProductType }) => {
    switch (id) {
        case 'PRO_DECK':
            return <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801765587_l8wmk3nhx_FastDeck.jpg" alt="Pro Decks" className="w-full h-full object-cover" />;
        case 'FAST_DECK':
            return <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801765494_k0i5hn1s0f_ProDeck.jpg" alt="Fast Decks" className="w-full h-full object-cover" />;
        case 'FUNDRAISING_PITCH_DECK':
            return <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775831165583_moejy2lid7m_fundraising.png" alt="Fundraising Pitch Decks" className="w-full h-full object-cover" />;
        case 'DOCUMENT':
            return <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801541214_9omio1q80zi_Document.jpg" alt="Documents" className="w-full h-full object-cover" />;
        case 'MARKET_RESEARCH':
            return <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801541060_9vd5sslpej_MarketResearch.jpg" alt="Market Research" className="w-full h-full object-cover" />;
        default:
            return <div className="w-full h-full bg-slate-100" />;
    }
}

export const CartProductList = ({ items, onUpdateQuantity, onSeeFeatures }: CartProductListProps) => {
    return (
        <div className="flex flex-col gap-4">
            {items.map((item) => (
                <div 
                    key={item.id} 
                    className="flex flex-col sm:flex-row items-center p-4 border border-slate-200 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] rounded-[15px] bg-white gap-5 transition-shadow hover:shadow-md"
                >
                    {/* Thumbnail */}
                    <div className="w-full sm:w-[160px] h-[100px] rounded-lg shrink-0 overflow-hidden">
                        <ProductThumbnail id={item.id} />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 pr-4">
                        <h3 className="font-bold text-slate-900 text-base mb-1 inline-flex items-center gap-2">
                            {item.name}
                        </h3>
                        <p className="text-[13px] text-slate-400 font-medium leading-relaxed mb-2.5">
                            {item.description}
                        </p>
                        <button 
                            onClick={() => onSeeFeatures?.(item.id)}
                            className="text-[13px] font-bold text-blue-500 hover:text-blue-600 transition-colors cursor-pointer"
                        >
                            See Features
                        </button>
                    </div>

                    {/* Price and Counter */}
                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto mt-4 sm:mt-0 gap-3">
                        <div className="text-lg font-bold text-slate-800">
                            {item.currencySymbol || '₹'}{item.price.toFixed(2)}
                        </div>
                        
                        <div className={cn(
                            "flex items-center border rounded-full overflow-hidden transition-all duration-300 shadow-sm",
                            item.quantity > 0 
                                ? "bg-blue-600 border-blue-600 text-white" 
                                : "bg-white border-blue-200 text-blue-500"
                        )}>
                            <button
                                onClick={() => onUpdateQuantity(item.id, -1)}
                                className={cn(
                                    "px-3 py-1.5 flex items-center justify-center transition-colors",
                                    item.quantity > 0 
                                        ? "text-white hover:bg-blue-700 cursor-pointer" 
                                        : "text-blue-200 cursor-not-allowed"
                                )}
                                disabled={item.quantity === 0}
                            >
                                <Minus className="w-3.5 h-3.5" />
                            </button>
                            
                            <div className={cn(
                                "w-8 text-center text-sm font-bold select-none",
                                item.quantity > 0 ? "text-white" : "text-blue-500"
                            )}>
                                {item.quantity.toString().padStart(2, '0')}
                            </div>
                            
                            <button
                                onClick={() => onUpdateQuantity(item.id, 1)}
                                className={cn(
                                    "px-3 py-1.5 flex items-center justify-center transition-colors cursor-pointer",
                                    item.quantity > 0 
                                        ? "text-white hover:bg-blue-700" 
                                        : "text-blue-500 hover:bg-blue-50"
                                )}
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {/* Custom Plan (Static) */}
            <div className="flex flex-col sm:flex-row items-center p-4 border border-slate-200 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] rounded-[15px] bg-slate-50 gap-5 transition-shadow hover:shadow-md">
                {/* Thumbnail */}
                {/* <div className="w-full sm:w-[160px] h-[100px] rounded-lg shrink-0 overflow-hidden bg-slate-900 flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950" />
                    <span className="relative text-white font-bold text-lg tracking-wider">CUSTOM</span>
                </div> */}

                {/* Details */}
                <div className="flex-1 min-w-0 pr-4">
                    <h3 className="font-bold text-slate-900 text-base mb-1 inline-flex items-center gap-2">
                        Custom Plan
                    </h3>
                    <p className="text-[13px] text-slate-500 font-medium leading-relaxed mb-2.5">
                        Contact us to get tailored solutions designed for your exact needs, with special discounts on bulk or custom orders. Tell us what you need, and our team will curate the right package for you- professional, flexible, and budget-friendly.

                    </p>
                </div>

                {/* Price and Contact */}
                <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto mt-4 sm:mt-0 gap-3">
                    
                    
                    <Link 
                        href="/contact?category=custom-plan"
                        className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-[13px] font-bold rounded-full transition-colors whitespace-nowrap"
                    >
                        Contact Us
                    </Link>
                </div>
            </div>
        </div>
    );
};
